<template>
  <div>
    <div v-if="!loading">
      <Lynx-Title :text="form.name" v-if="form.ean && form.name" v-once />
      <Lynx-Title text="Adicionar Produto" v-else />
      <form class="flex flex-col">
        <label class="w-full block px-1 tracking-wide text-gray-700 text-xs">
          <Lynx-Label label="Ean" />
          <Lynx-Input
            placeholder="Insira o ean"
            maxlength="20"
            :disabled="Boolean(this.productEan)"
            :error="$v.form.ean.$error"
            v-model.trim="$v.form.ean.$model"
            :validator="$v.form.ean"
            :validations-message="validationsMessage.form.ean"
          />
        </label>
        <label class="w-full block px-1 tracking-wide text-gray-700 text-xs">
          <Lynx-Label label="Produto" />
          <Lynx-Input
            placeholder="Insira o produto"
            :error="$v.form.product.$error"
            v-model.trim="$v.form.product.$model"
            :validator="$v.form.product"
            :validations-message="validationsMessage.form.product"
          />
        </label>
        <label class="w-full block px-1 tracking-wide text-gray-700 text-xs">
          <Lynx-Label label="Preço Sugerido" />
          <Lynx-Input
            placeholder="Insira o preço"
            money-mask="true"
            :error="$v.form.suggestedPrice.$error"
            v-model.trim="$v.form.suggestedPrice.$model"
            :validator="$v.form.suggestedPrice"
            :validations-message="validationsMessage.form.suggestedPrice"
          />
        </label>
        <div class="w-full block px-1 tracking-wide text-gray-700 text-xs">
          <Lynx-Label label="imagem" />
          <UploadFile
            @upload="onSubmit"
            :uploadSuccess="uploadSuccess"
            :fileData="fileData"
            :loading="loadingButton"
            goBackUrl="products.index"
            isEdit
            :defaultFile="form.file"
          />
        </div>
      </form>
    </div>
    <Lynx-OverlaySpinner v-else :loading="loading" />
  </div>
</template>

<script>
import {
  productsCreateRequest,
  productsUpdateRequest,
  productsShowRequest,
} from "../../services/requests/products";

import { getFileByPathAndToken } from "../../services/requests/files";
import { required } from "vuelidate/lib/validators";
import {
  validateImageType,
  convertBase64,
  arrayBufferToFile,
} from "../../utils/base64";
import UploadFile from "../../components/shared/UploadFile";
import { mapGetters } from "vuex";

export default {
  name: "ProductsCreate",
  components: { UploadFile },
  data() {
    return {
      productEan: this.$route.params.ean ?? null,
      fileId: null,
      form: {
        ean: null,
        product: null,
        suggestedPrice: null,
        file: null,
      },
      uploadSuccess: false,
      fileData: {
        type: "JPG, JPEG, PNG, GIF, BMP ou WEBP",
        mimeType: [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/bmp",
          "image/webp",
        ],
      },
      loading: false,
      loadingButton: false,
      validationsMessage: {
        form: {
          product: {
            required: "Insira o nome do produto",
          },
          ean: {
            required: "Insira o ean do produto",
          },
          suggestedPrice: {
            required: "Insira o preço sugerido do produto",
          },
          file: {
            required: "Insira um arquivo",
          },
        },
      },
    };
  },
  validations() {
    return {
      form: {
        ean: { required },
        product: { required },
        suggestedPrice: { required },
        file: { required },
      },
    };
  },
  computed: {
    ...mapGetters(["getToken"]),
  },
  methods: {
    getErrors(error) {
      const errors = Object.values(error.response.data.errors);
      const message = errors
        .map((item) => item.map((error) => `<li>${error}</li>`).join(""))
        .join("");

      this.$buefy.dialog.alert({
        title: "Não foi possível salvar o Produto",
        message: `<ul>${message}</ul>`,
        type: "is-danger",
        ariaRole: "alertdialog",
        ariaModal: true,
      });
    },
    async fetchProduct() {
      this.loading = true;
      await productsShowRequest(this.productEan)
        .then((res) => {
          const data = res.data.data;
          this.form = {
            ean: data.ean,
            product: data.product,
            suggestedPrice: data.suggested_price,
          };
          if (data.file) {
            this.fileId = data.file.id;
            getFileByPathAndToken(data.file.path, this.getToken)
              .then((file) => {
                this.form.file = arrayBufferToFile(
                  file.data,
                  data.file.extension,
                  data.file.name
                );
              })
              .catch((error) => {
                this.getErrors(error);
              })
              .finally(() => (this.loading = false));
          }
        })
        .catch(() => {
          this.loading = false;
          this.$router.push({ name: "products.index" });
          this.$snack.error("Produto não encontrado");
        })
        .finally(() => (this.loading = false));
    },
    async onSubmit(file) {
      this.form.file = file;
      await this.$v.$touch();
      this.uploadSuccess = false;

      if (this.$v.form.$invalid) {
        this.$snack.error("Preencha corretamente o formulário");
        return;
      }

      if (!validateImageType(this.form.file)) {
        this.$snack.error("O arquivo deve ser uma imagem");
        return;
      }

      this.loadingButton = true;
      if (!this.form.role) {
        this.form.role = "customer";
      }

      convertBase64(this.form.file).then((base64) => {
        const dataForSubmit = {
          product: this.form.product,
          suggested_price: String(this.form.suggestedPrice),
          file: {
            id: this.fileId,
            label: "product",
            name: this.form.file.name,
            base64,
          },
        };
        if (!this.productEan) {
          productsCreateRequest({
            ...dataForSubmit,
            action: "store",
            ean: this.form.ean,
          })
            .then((res) => {
              this.uploadSuccess = true;
              this.$snack.success("Produto salvo com sucesso");
              this.$router.push({ name: "products.index" });
            })
            .catch((error) => {
              this.getErrors(error);
            })
            .finally(() => (this.loadingButton = false));
        } else {
          productsUpdateRequest(
            { ...dataForSubmit, action: "update" },
            this.form.ean
          )
            .then((res) => {
              this.$snack.success("Produto salvo com sucesso");
              this.$router.push({ name: "products.index" });
            })
            .catch((error) => {
              this.getErrors(error);
            })
            .finally(() => (this.loadingButton = false));
        }
      });
    },
  },
  async mounted() {
    if (this.productEan) {
      await this.fetchProduct();
    }
  },
};
</script>
